<template>

<!--    Phone verification component - modal-->
<!--    phone confirmation component when is NOT modal-->
<!--    this component is gonna be shown if user has not confirmed phone number yet and selected product requires phone verification.-->
    <div v-if="showPhoneConfirmation && phoneConfirmationIsModal" class="modal" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-dialog-centered my-0" role="document">
            <div class="modal-content">
                <div class="modal-body p-4">
                    <PhoneConfirmation
                        @phoneConfirmed="handlePhoneConfirmation"
                        @phoneConfirmationFailed="handlePhoneConfirmationFailed"
                        :phoneNumberProp="maskedPhoneNumber"
                        :uid="uid"
                        :isModal="true" ref="phoneConfirmationComponent"
                        :canConfirmPhone="canConfirmPhone"
                        :maxFailedAttempts="settings.phoneConfirmationMaxFailedAttempts"
                        :failedAttempts="phoneConfirmationFailedAttempts"
                    />
                </div>
                <div class="modal-footer px-4 d-flex justify-content-between">
                    <a href="/loan" target="_blank" class="btn btn-sm btn-outline-primary w-auto m-0">New Application</a>
                    <div class="d-flex m-0">
                        <button type="button" class="btn btn-secondary w-auto ms-2" :class="{'btn-sm' : settings.isMobile}" @click="emit('updateSettings', { selectedProduct: null })">Close</button>
                        <button v-if="canConfirmPhone" type="button" class="btn btn-success w-auto ms-2" :class="{'btn-sm' : settings.isMobile}" @click="phoneConfirmationComponent.doConfirmPhoneRequest()">Verify</button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Container -->
    <div class="container-fluid p-0" style="min-height: 100%;">

        <!-- Navitation Bar -->
        <nav class="navbar navbar-expand-lg navbar-light bg-white shadow-sm p-0">
            <div class="container">
                <a class="navbar-brand p-0 mx-auto me-auto" href="https://loanscanada.ca/" title="CompareHub by LoansCanada">
                    <img alt="Loans Canada Logo" class="my-2 mx-auto" src="https://app.loanscanada.ca/assets/images/compare-hub-logo.png" style="height:3.5rem; width:auto;">
                </a>
                <template v-if="settings.isPhoneConfirmed">
                    <button class="btn btn-toggle-menu btn-outline-primary position-absolute" @click="toggleSidebar()" style="top: 50%; right: 15px; transform: translateY(-50%);">
                        <i class="fa fa-bars my-1" style="font-size: 18px;"></i>
                    </button>
                </template>
            </div>
        </nav>

        <!-- Main Content -->
        <div class="px-3 px-md-5 page-main-content" style="min-height: 100vh;">
            <div class="featured-content-bg py-1 py-md-2">

                <div v-if="settings.container.title" class="my-0 pt-4 pt-md-4 text-center">
                    <h3 class="m-0">{{settings.container.title}}</h3>
                </div>

                <div class="page-product-selection my-3 my-md-4 pt-0 pt-md-0">

<!--                    ProductSelection component-->
<!--                    first component to be shown. user has to select a product to continue.-->
<!--                    once this product is selected we determine if phone confirmation is required and show corresponding component.-->
<!--                    if user clicks on  "Back to product selection" button, we have to set isProductSelected to false so this component is shown again.-->
                    <div v-if="showProductSelection">
                        <div class="row">
                            <div :class="elementsWidthClass">
                                <ProductSelection @selectedProduct="selectProduct" :isPhoneConfirmed="settings.isPhoneConfirmed" :hasFailedPhoneConfirmation="settings.hasFailedPhoneConfirmation" :memberPortalUrl="settings.memberPortalUrl"/>
                            </div>
                        </div>
                    </div>

                    <div v-if="settings.container.showOtherOffers">
                        <div class="row">
                            <div class="text-center" :class="elementsWidthClass">
                                <HardcodedOffers :uid="uid" />
                                <div v-if="showBackToProductsButton">
<!--                                    <div class="my-0 text-end" :class="elementsWidthClass">-->
                                    <div class="my-0 px-3 text-end">
                                        <button type="button" @click.prevent="$emit('menuOptionSelected', 'product_selection')" class="btn btn-primary w-100 text-uppercase fw-bold">Back to Product Selection</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row" v-if="showReviewData">
                        <div :class="elementsWidthClass">
                            <div class="card shadow my-2 bg-body rounded h-auto py-4 px-3 py-md-4 px-md-5">
                                <ReviewData :uid="uid" :selectedProduct="settings.selectedProduct"/>
                            </div>
                        </div>
                    </div>

                </div>

            </div>
        </div>

    </div>

</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import ProductSelection from './ProductSelection.vue';
import PhoneConfirmation from './PhoneConfirmation.vue';
import ReviewData from './ReviewData.vue';
import HardcodedOffers from './HardcodedOffers.vue';
import { useRefreshResultsStorage } from '@/composables/useRefreshResultsStorage.js';

const props = defineProps({
    settings: {
        type: Object,
        required: true,
    },
    maskedPhoneNumber: {
        type: String,
        required: false,
        default: '',
    },
    uid: {
        type: String,
        required: false,
        default: '',
    },
});

const emit = defineEmits(['menuOptionSelected', 'updateSettings']);

const { get: getStoredResults } = useRefreshResultsStorage(props.uid);
const phoneConfirmationComponent = ref(null);
const phoneConfirmationIsModal = ref(true);
const phoneConfirmationFailedAttempts = ref(0);
const containerTitle = ref(null);
const productsThatNeedReviewData = ['personal_loan', 'car_loan'];

const showPhoneConfirmation = computed(() => {
    const productsThatRequirePhoneConfirmation = ['personal_loan', 'car_loan'];
    return isProductSelected.value && !props.settings.isPhoneConfirmed && productsThatRequirePhoneConfirmation.includes(props.settings.selectedProduct);
});

const canConfirmPhone = computed(() => {
    return isProductSelected.value && !props.settings.isPhoneConfirmed && !props.settings.hasFailedPhoneConfirmation;
});

const showProductSelection = computed(() => {
    return !isProductSelected.value;
});

const isProductSelected = computed(() => {
    return !!props.settings.selectedProduct;
});

const showReviewData = computed(() => {
    return isProductSelected.value && props.settings.isPhoneConfirmed && productsThatNeedReviewData.includes(props.settings.selectedProduct);
});

const elementsWidthClass = computed(() => {
    if (props.settings.isMobile) {
        return 'col-12';
    } else {
        return props.settings.isPhoneConfirmed ? 'col-lg-10 offset-lg-1 col-xl-6 offset-xl-3' : 'col-lg-8 offset-lg-2 col-xl-6 offset-xl-3';
    }
});

const showBackToProductsButton = computed(() => {
    return props.settings.selectedProduct === 'other_offers';
});

onMounted(() => {
    const localStorageData = getStoredResults();
    if (localStorageData.phoneNumberConfirmed) {
        handlePhoneConfirmation();
        emit('updateSettings', { container: { title: null } });
    }
});

function toggleSidebar() {
    emit('updateSettings', { sidebar: { isActive: !props.settings.sidebar.isActive } });
}

function selectProduct(product) {
    const updates = { selectedProduct: product, container: { showOtherOffers: false } };

    switch (product) {
        case 'other_offers':
            updates.container.showOtherOffers = true;
            break;
        case '1_click_application':
            alert('submitting 1-click application');
            break;
        case 'personal_loan':
        case 'car_loan':
            break;
        default:
            break;
    }

    emit('updateSettings', updates);
}

function handlePhoneConfirmation() {
    emit('updateSettings', {
        hasFailedPhoneConfirmation: false,
        isPhoneConfirmed: true,
        sidebar: { isActive: true },
        container: { title: "Thanks for confirming your phone number!" },
    });
    phoneConfirmationFailedAttempts.value = 0;
}

function handlePhoneConfirmationFailed(failedAttemptsIncrease = 1) {
    phoneConfirmationFailedAttempts.value += failedAttemptsIncrease;
    emit('updateSettings', {
        isPhoneConfirmed: false,
        sidebar: { isActive: false },
        hasFailedPhoneConfirmation: phoneConfirmationFailedAttempts.value >= props.settings.phoneConfirmationMaxFailedAttempts,
    });
}
</script>

<style scoped>
.modal {
display: block; /* Makes it visible when active */
position: fixed;
top: 0;
left: 0;
width: 100%;
height: 100%;
background-color: rgba(0, 0, 0, 0.5);
z-index: 9999;
}

.modal-dialog {
position: relative;
top: 50%;
transform: translateY(-50%);
}
</style>
